import { useContext } from 'react'
import { ProfileContext } from 'providers/profile'
import {
  hasIBAAccess,
  isFromIBAEligibleOrganizations,
  isCountryIBAActive,
  getIBAAccessLevel,
  hasIBAAccessForBirds,
  hasIBAFeatureAccess,
  getIBADataDisplayOption
} from 'utils/ibaAccess'
import {
  hasSiteIBAAccess,
  getSiteIBAAccessLevel,
  hasSiteIBAFeatureAccess,
  getSiteIBADataDisplayOption,
  hasSiteIBAAccessForBirds
} from 'utils/siteIbaAccess'
import { IBA_FEATURES, IBA_DATA_DISPLAY } from 'constants/ibaAccess'

/**
 * Custom hook for IBA access control
 * Provides consistent IBA access logic across components
 * Now supports both user-based and site-based IBA access
 */
export const useIBAAccess = () => {
  const { profile } = useContext(ProfileContext)

  return {
    // Basic access checks (user-based - legacy)
    hasIBAAccess: hasIBAAccess(profile),
    isFromIBAEligibleOrganizations: isFromIBAEligibleOrganizations(profile),
    isCountryIBAActive: isCountryIBAActive(profile),

    // Access level (user-based - legacy)
    accessLevel: getIBAAccessLevel(profile),

    // Specific access checks (user-based - legacy)
    hasIBAAccessForBirds: (speciesClassName = 'AVES') => hasIBAAccessForBirds(profile, speciesClassName),

    // Feature visibility (user-based - legacy)
    canSeeIBACriteria: hasIBAFeatureAccess(profile, IBA_FEATURES.CRITERIA),
    canSeeIBACongregations: hasIBAFeatureAccess(profile, IBA_FEATURES.CONGREGATIONS),
    canSeeIBASpeciesFields: hasIBAFeatureAccess(profile, IBA_FEATURES.SPECIES_FIELDS),
    canSeeIBASitesManagement: hasIBAFeatureAccess(profile, IBA_FEATURES.SITES_MANAGEMENT),

    // Generic feature access checker with validation (user-based - legacy)
    canSeeIBAFeature: (feature) => hasIBAFeatureAccess(profile, feature),
    
    // Data display logic (user-based - legacy)
    shouldShowIBAData: (data, feature = IBA_FEATURES.SITES_MANAGEMENT) => {
      const displayOption = getIBADataDisplayOption(profile, feature)

      switch (displayOption) {
        case IBA_DATA_DISPLAY.SHOW_DATA:
          return data || '-'
        case IBA_DATA_DISPLAY.SHOW_DASH:
          return '-'
        case IBA_DATA_DISPLAY.HIDE:
        default:
          return null
      }
    },

    // Legacy method for backward compatibility
    shouldShowIBADataLegacy: (data) => {
      const accessLevel = getIBAAccessLevel(profile)
      if (accessLevel === 'full') {
        return data || '-'
      } else if (accessLevel === 'limited') {
        return '-' // Show dash for limited access
      } else {
        return null // Don't show field at all
      }
    },

    // NEW: Site-based IBA access methods
    // Basic site-based access checks
    hasSiteIBAAccess: (site) => hasSiteIBAAccess(profile, site),

    // Site-based access level
    getSiteIBAAccessLevel: (site) => getSiteIBAAccessLevel(profile, site),

    // Site-based specific access checks
    hasSiteIBAAccessForBirds: (site, speciesClassName = 'AVES') => hasSiteIBAAccessForBirds(profile, site, speciesClassName),

    // Site-based feature visibility
    canSeeSiteIBACriteria: (site) => hasSiteIBAFeatureAccess(profile, site, IBA_FEATURES.CRITERIA),
    canSeeSiteIBACongregations: (site) => hasSiteIBAFeatureAccess(profile, site, IBA_FEATURES.CONGREGATIONS),
    canSeeSiteIBASpeciesFields: (site) => hasSiteIBAFeatureAccess(profile, site, IBA_FEATURES.SPECIES_FIELDS),
    canSeeSiteIBASitesManagement: (site) => hasSiteIBAFeatureAccess(profile, site, IBA_FEATURES.SITES_MANAGEMENT),

    // Site-based generic feature access checker
    canSeeSiteIBAFeature: (site, feature) => hasSiteIBAFeatureAccess(profile, site, feature),
    
    // Site-based data display logic
    shouldShowSiteIBAData: (site, data, feature = IBA_FEATURES.SITES_MANAGEMENT) => {
      const displayOption = getSiteIBADataDisplayOption(profile, site, feature)

      switch (displayOption) {
        case IBA_DATA_DISPLAY.SHOW_DATA:
          return data || '-'
        case IBA_DATA_DISPLAY.SHOW_DASH:
          return '-'
        case IBA_DATA_DISPLAY.HIDE:
        default:
          return null
      }
    }
  }
}

export default useIBAAccess
